/**
 * This tenant's PDApps, and which ones open without the splash (#538).
 *
 * The splash is what a visitor sees before a PDApp link opens: who runs it, Report, and the identity
 * question. A platform admin can mark a PDApp they trust "Opens without splash" — every link to it then
 * goes straight in, and (owner, 2026-11-03) is allowed every identity field it asks for without asking
 * the visitor (`grantValidatedPdappConsent` — a decline the visitor makes stands; this screen only sets the mark).
 *
 * The mark is for the PDApp, not one version of it — it survives the author's edits (owner,
 * 2026-11-02) — so each row shows when the PDApp last changed, and a change after it was marked is
 * called out.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast } from '@papan/core-ui/ui'
import { budgetApi } from '../../client/budget'

interface Row {
  appFolderId: string
  name: string
  author: { name: string | null; email: string | null } | null
  lastChangedAt: string | null
  opensWithoutSplash: { by: string; at: string } | null
}

export function CustomerPdapps({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [rows, setRows] = useState<Row[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [query, setQuery] = useState('')
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await budgetApi.getTenantPdapps(tenantId)
      setRows(res.pdapps)
      setTruncated(res.truncated)
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }, [tenantId])

  useEffect(() => { setRows(null); setQuery(''); load() }, [load])

  // By the PDApp's name or its author's name or email — every word must match somewhere.
  const shown = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean)
    if (!rows || words.length === 0) return rows
    return rows.filter((r) => {
      const hay = [r.name, r.author?.name, r.author?.email].filter(Boolean).join(' ').toLowerCase()
      return words.every((w) => hay.includes(w))
    })
  }, [rows, query])

  const toggle = async (row: Row) => {
    setBusy(row.appFolderId)
    try {
      await budgetApi.setOpensWithoutSplash(tenantId, row.appFolderId, !row.opensWithoutSplash)
      await load()
    } catch (e: any) {
      toast.error(e?.message || t('settings.platform.pdapps.saveFailed'))
    } finally {
      setBusy(null)
    }
  }

  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

  if (failed) return <p className="themed-text-muted text-sm">{t('settings.platform.pdapps.loadFailed')}</p>
  if (!rows) return <p className="themed-text-muted text-sm">{t('settings.platform.pdapps.loading')}</p>

  return (
    <div className="themed-bg-secondary rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b themed-border">
        <div className="themed-text text-sm">{t('settings.platform.pdapps.title')}</div>
        <p className="themed-text-muted text-[11px] mt-0.5">{t('settings.platform.pdapps.desc')}</p>
        {rows.length > 0 && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('settings.platform.pdapps.search')}
            aria-label={t('settings.platform.pdapps.search')}
            className="mt-2 w-full max-w-sm px-2 py-1 text-sm rounded themed-input"
          />
        )}
        {truncated && <p className="themed-text-muted text-[11px] mt-1">{t('settings.platform.pdapps.truncated')}</p>}
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 themed-text-muted text-sm">{t('settings.platform.pdapps.none')}</p>
      ) : shown!.length === 0 ? (
        <p className="px-3 py-3 themed-text-muted text-sm">{t('settings.platform.pdapps.noMatch')}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="themed-text-muted text-[11px] text-left border-b themed-border">
              <th className="px-3 py-1.5 font-normal">{t('settings.platform.pdapps.colName')}</th>
              <th className="px-3 py-1.5 font-normal">{t('settings.platform.pdapps.colAuthor')}</th>
              <th className="px-3 py-1.5 font-normal">{t('settings.platform.pdapps.colLastChanged')}</th>
              <th className="px-3 py-1.5 font-normal">{t('settings.platform.pdapps.colOpensWithoutSplash')}</th>
            </tr>
          </thead>
          <tbody>
            {shown!.map((row) => {
              const mark = row.opensWithoutSplash
              const changedSince = !!(mark && row.lastChangedAt && new Date(row.lastChangedAt) > new Date(mark.at))
              return (
                <tr key={row.appFolderId} className="border-b themed-border last:border-b-0 align-top">
                  <td className="px-3 py-2">
                    <div className="themed-text truncate">{row.name}</div>
                    <div className="text-[11px] themed-text-muted font-mono truncate">{row.appFolderId}</div>
                  </td>
                  <td className="px-3 py-2 min-w-0">
                    {row.author ? (
                      <>
                        <div className="themed-text text-[12px] truncate">{row.author.name ?? row.author.email}</div>
                        {row.author.name && row.author.email && (
                          <div className="text-[11px] themed-text-muted truncate">{row.author.email}</div>
                        )}
                      </>
                    ) : (
                      <span className="themed-text-muted text-[12px]">{t('settings.platform.pdapps.authorUnknown')}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 themed-text-muted text-[12px] whitespace-nowrap">
                    {date(row.lastChangedAt)}
                    {changedSince && (
                      <div className="text-[11px] text-amber-500">{t('settings.platform.pdapps.changedSince')}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!mark}
                        disabled={!canEdit || busy === row.appFolderId}
                        onChange={() => toggle(row)}
                        aria-label={t('settings.platform.pdapps.colOpensWithoutSplash')}
                      />
                      <span className="themed-text text-[12px]">
                        {mark ? t('settings.platform.pdapps.on') : t('settings.platform.pdapps.off')}
                      </span>
                    </label>
                    {mark && (
                      <div className="text-[11px] themed-text-muted">
                        {t('settings.platform.pdapps.markedBy', { who: mark.by, when: date(mark.at) })}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
